import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle, 
  Loader2, 
  X, 
  Shield, 
  Users, 
  Clock,
  ArrowRight,
  User,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================
// LEAD CAPTURE CTA - Shown after analysis
// ==================
export function LeadCaptureCTA({ 
  analysisType, 
  analysisId, 
  projectSummary,
  analysisContent,
  onLeadCaptured 
}) {
  const [showModal, setShowModal] = useState(false);
  const { isPremium } = useAuth();

  // Premium users don't see lead capture
  if (isPremium) return null;

  return (
    <>
      {/* CTA Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-6 p-5 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-2 border-emerald-500/30 rounded-xl"
      >
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex-shrink-0 w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center">
            <Users className="w-7 h-7 text-emerald-400" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <h4 className="text-white font-bold text-lg mb-1">
              Recevez des devis d'artisans qualifiés
            </h4>
            <p className="text-zinc-400 text-sm">
              Gagnez du temps et comparez les prix avec des professionnels vérifiés pour votre projet.
            </p>
          </div>
          <Button
            onClick={() => setShowModal(true)}
            className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-3 px-6"
            data-testid="request-quotes-btn"
          >
            Recevoir mes devis gratuits
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {/* Trust indicators */}
        <div className="mt-4 pt-4 border-t border-emerald-500/20 flex flex-wrap justify-center sm:justify-start gap-4 text-xs text-zinc-500">
          <span className="flex items-center gap-1">
            <Shield className="w-3 h-3 text-emerald-400" />
            Artisans vérifiés
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-emerald-400" />
            Réponse sous 48h
          </span>
          <span className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-emerald-400" />
            Service gratuit
          </span>
        </div>
      </motion.div>

      {/* Modal */}
      <LeadCaptureModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        analysisType={analysisType}
        analysisId={analysisId}
        projectSummary={projectSummary}
        analysisContent={analysisContent}
        onSuccess={() => {
          setShowModal(false);
          if (onLeadCaptured) onLeadCaptured();
        }}
      />
    </>
  );
}

// ==================
// LEAD CAPTURE MODAL - Simple form
// ==================
function LeadCaptureModal({ 
  isOpen, 
  onClose, 
  analysisType, 
  analysisId, 
  projectSummary,
  analysisContent,
  onSuccess 
}) {
  const { user, getAuthHeaders } = useAuth();
  
  // Form state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [villeProjet, setVilleProjet] = useState('');
  const [consent, setConsent] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Empêcher double submit
    if (loading) return;
    
    setError('');
    
    // Validate
    if (!name.trim() || name.trim().length < 2) {
      setError('Veuillez entrer votre prénom ou pseudo');
      return;
    }
    
    if (!email.trim() || !email.includes('@')) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }
    
    if (!phone.trim() || phone.replace(/\s/g, '').length < 10) {
      setError('Veuillez entrer un numéro de téléphone valide');
      return;
    }
    
    if (!villeProjet.trim() || villeProjet.trim().length < 2) {
      setError('Veuillez renseigner la ville de votre projet');
      return;
    }
    
    if (!consent) {
      setError('Vous devez accepter d\'être contacté par des artisans');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/leads/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          ville_projet: villeProjet.trim(),
          analysis_type: analysisType,
          analysis_id: analysisId,
          project_summary: projectSummary,
          analysis_content: analysisContent,
          consent_artisan_contact: true
        })
      });
      
      // Clone response pour éviter "Body is disturbed or locked"
      const responseClone = response.clone();
      let data;
      
      try {
        data = await response.json();
      } catch (parseError) {
        // Si le parsing échoue, essayer avec le clone
        try {
          data = await responseClone.json();
        } catch {
          throw new Error('Une erreur est survenue lors de l\'envoi de votre demande. Veuillez réessayer.');
        }
      }
      
      if (!response.ok) {
        // Message d'erreur user-friendly
        const errorMsg = data.detail || 'Une erreur est survenue lors de l\'envoi de votre demande. Veuillez réessayer.';
        throw new Error(typeof errorMsg === 'string' ? errorMsg : 'Une erreur est survenue. Veuillez réessayer.');
      }
      
      setSuccess(true);
      
      // Close after showing success message
      setTimeout(() => {
        onSuccess();
      }, 3000);
      
    } catch (err) {
      // Message d'erreur user-friendly (jamais d'erreur technique brute)
      const userMessage = err.message.includes('fetch') || err.message.includes('network') || err.message.includes('Body')
        ? 'Une erreur est survenue lors de l\'envoi de votre demande. Veuillez réessayer.'
        : err.message;
      setError(userMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setName(user?.name || '');
      setEmail(user?.email || '');
      setPhone('');
      setVilleProjet('');
      setConsent(false);
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Recevoir des devis</h3>
                  <p className="text-zinc-500 text-sm">Gratuit et sans engagement</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-zinc-500 hover:text-white transition-colors"
                disabled={loading}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {success ? (
              /* Success State */
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
                <h4 className="text-white font-bold text-xl mb-2">
                  Demande envoyée !
                </h4>
                <p className="text-zinc-400">
                  Des artisans vont vous contacter rapidement pour votre projet.
                </p>
              </motion.div>
            ) : (
              /* Form */
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="text-zinc-400 text-sm mb-1.5 block">
                    Prénom ou pseudo *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Votre prénom"
                      className="pl-10 bg-zinc-800/50 border-zinc-700 text-white"
                      disabled={loading}
                      data-testid="lead-name-input"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="text-zinc-400 text-sm mb-1.5 block">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      className="pl-10 bg-zinc-800/50 border-zinc-700 text-white"
                      disabled={loading}
                      data-testid="lead-email-input"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="text-zinc-400 text-sm mb-1.5 block">
                    Téléphone *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="06 12 34 56 78"
                      className="pl-10 bg-zinc-800/50 border-zinc-700 text-white"
                      disabled={loading}
                      data-testid="lead-phone-input"
                    />
                  </div>
                </div>

                {/* Ville du projet */}
                <div>
                  <label className="text-zinc-400 text-sm mb-1.5 block">
                    Ville du projet *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-500" />
                    <Input
                      type="text"
                      value={villeProjet}
                      onChange={(e) => setVilleProjet(e.target.value)}
                      placeholder="Ex : Nancy, Lyon, Paris..."
                      className="pl-10 bg-zinc-800/50 border-zinc-700 text-white focus:border-amber-500"
                      disabled={loading}
                      data-testid="lead-ville-projet-input"
                    />
                  </div>
                  <p className="text-zinc-500 text-xs mt-1">Où se situe votre projet ?</p>
                </div>

                {/* Project info (read-only) */}
                <div className="bg-zinc-800/30 rounded-lg p-3 border border-zinc-700/50">
                  <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Votre projet</p>
                  <p className="text-zinc-300 text-sm line-clamp-2">
                    {projectSummary || `Demande de type: ${analysisType}`}
                  </p>
                </div>

                {/* Consent checkbox */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative mt-0.5">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="sr-only peer"
                      disabled={loading}
                      data-testid="lead-consent-checkbox"
                    />
                    <div className={`w-5 h-5 rounded border-2 transition-colors ${
                      consent 
                        ? 'bg-emerald-500 border-emerald-500' 
                        : 'border-zinc-600 group-hover:border-zinc-500'
                    }`}>
                      {consent && <CheckCircle className="w-4 h-4 text-white absolute top-0.5 left-0.5" />}
                    </div>
                  </div>
                  <span className="text-zinc-400 text-sm leading-tight">
                    J'accepte d'être contacté par des artisans partenaires pour ce projet *
                  </span>
                </label>

                {/* Error */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm"
                  >
                    {error}
                  </motion.div>
                )}

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading || !consent}
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold py-3"
                  data-testid="lead-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      Envoyer ma demande
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                {/* Privacy note */}
                <p className="text-zinc-600 text-xs text-center">
                  Vos données sont protégées et ne seront partagées qu'avec des artisans qualifiés.
                </p>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default LeadCaptureCTA;
